import { currencyIconUrl } from "../hub/econArt";
import { gearIconUrl, resourceIconUrl, uiUrl } from "../portraits";
import { PaintedIcon, paintedIconUrl } from "../featureIcons";
import { supplyIconUrl } from "../supplies/supplyArt";
import { petPortraitUrl } from "../pets/petArt";
import { treatArtHtml } from "../pets/treatArt";
import { isSupplyId } from "@highmarch/domain";
import type { Gain } from "./holdings";
import type { RewardKind } from "./rewardHomes";

/**
 * The picture each flying reward wears: the same art the screens use for it (the gem, a gear piece in its slot and
 * rarity, the pet's portrait, the treat's biscuit). Every kind has one (a Record, so a new kind cannot forget it).
 */

/** Sigils are painted up to Legendary; a Mythic sigil wears the Legendary one. */
const SIGIL_ICON: Readonly<Record<string, PaintedIcon>> = {
  common: "sigilCommon", uncommon: "sigilUncommon", rare: "sigilRare", epic: "sigilEpic", legendary: "sigilLegendary", mythic: "sigilLegendary",
};
/** The treats' art is inline SVG: drawn at this size into a data URL once per treat. */
const TREAT_ART_PX = 64;
const treatUrls = new Map<string, string>();

function treatUrl(id: string): string {
  let u = treatUrls.get(id);
  if (!u) { u = `data:image/svg+xml,${encodeURIComponent(treatArtHtml(id, TREAT_ART_PX))}`; treatUrls.set(id, u); }
  return u;
}

/** `gear:<id>:<slot>:<rarity>` → the painted piece. */
function gearUrl(variant: string): string {
  const [, slot = "", rarity = "common"] = variant.split(":");
  return gearIconUrl(slot, rarity);
}

const ICONS: Readonly<Record<RewardKind, (variant: string) => string>> = {
  gems: () => currencyIconUrl("gems"),
  gold: () => currencyIconUrl("gold"),
  marks: () => currencyIconUrl("marks"),
  trophies: () => currencyIconUrl("trophies"),
  essence: () => paintedIconUrl("essence"),
  sigil: (v) => paintedIconUrl(SIGIL_ICON[v] ?? "sigilCommon"),
  gear: gearUrl,
  speedUp: () => resourceIconUrl("speedup"),
  supply: (v) => (isSupplyId(v) ? supplyIconUrl(v) : paintedIconUrl("crate")),
  pet: (v) => petPortraitUrl(v),
  treat: treatUrl,
  skin: () => paintedIconUrl("skins"),
  emblem: () => paintedIconUrl("skins"),
  embers: () => paintedIconUrl("eventEmber"),
  builder: () => uiUrl("icons/event_builders.png"),
  vipXp: () => uiUrl("icons/vip_points.png"),
};

export function rewardIconUrl(g: Pick<Gain, "kind" | "variant">): string { return ICONS[g.kind](g.variant); }
